import { useCheckoutStore } from '~/store/checkout'
import type { CheckoutInvalidateResponseInterface } from '~/types/checkoutTypes'
import { useCartStore } from '~/store/cart'
import { PagesPrefixEnum } from '~/enums/PagesPrefixEnum'
import { useLanguageStore } from '~/store/language'
import { useLoaderStore } from '~/store/loader'
import type { RouteLocationNormalized } from 'vue-router'
import { Routes } from '~/enums/Routes'
import { CheckoutRoutes } from '~/enums/CheckoutRoutes'
import getLocalizedRoute from '~/utils/getLocalizedRoute'
import { fetchRetryParameters, requestHostHeaders, type RequestHostHeadersType } from '~/composables/constants/api'

function isRouteNameChanged(to: RouteLocationNormalized, from: RouteLocationNormalized) {
  if (to.name !== from.name) {
    return true
  }

  if (to.name === from.name && dropLocalePrefixFromRouteName(String(to.name)) === Routes.Checkout.ShippingSlug) {
    const toSlug = getSlug(to)
    const fromSlug = getSlug(from)

    if (toSlug !== fromSlug && toSlug === null) {
      return true
    }
  }

  return false
}

function getSlug(route: RouteLocationNormalized): string | null {
  const slug = route.params.slug || null

  if (slug) {
    return [...slug].join('')
  }

  return null
}

function dropLocalePrefixFromRouteName(routeName: string) {
  return routeName.replace(PagesPrefixEnum, '')
}

function shouldInvalidate(from: RouteLocationNormalized, to: RouteLocationNormalized) {
  const fromNameWithoutPrefix = dropLocalePrefixFromRouteName(String(from.name))
  if (!CheckoutRoutes.includes(fromNameWithoutPrefix)) {
    return false
  }

  const toNameWithoutPrefix = dropLocalePrefixFromRouteName(String(to.name))

  if (CheckoutRoutes.includes(toNameWithoutPrefix)) {
    if (fromNameWithoutPrefix === toNameWithoutPrefix && Routes.Checkout.ShippingSlug === toNameWithoutPrefix) {
      const toSlug = getSlug(to)
      const fromSlug = getSlug(from)

      if (toSlug === null && fromSlug) {
        return true
      }
    }

    return false
  }

  return true
}

export default defineNuxtRouteMiddleware(async (to, from) => {
  if (!isRouteNameChanged(to, from)) {
    return
  }

  if (!shouldInvalidate(from, to)) {
    return
  }

  const checkoutStore = useCheckoutStore()

  const loaderStore = useLoaderStore()
  loaderStore.setLoader(true)

  const slug = checkoutStore.slug

  if (slug) {
    await invalidateOrder(slug)

    if (dropLocalePrefixFromRouteName(String(from.name)) === Routes.Checkout.ShippingSlug) {
      return navigateTo(getLocalizedRoute(Routes.Checkout.Cart))
    }
  }

  async function invalidateOrder(slug: string) {
    interface formDataInterface {
      slug: string | null
      language_id: number | null
    }

    const formData: formDataInterface = {
      slug,
      language_id: useLanguageStore().getLanguageId,
    }

    const response = await $fetch<CheckoutInvalidateResponseInterface>(`/api/checkout/invalidate/${slug}`, {
      method: 'POST',
      body: formData,
      ...fetchRetryParameters,
      retry: 2,
      headers: useRequestHeaders<RequestHostHeadersType>(requestHostHeaders),
    })

    checkoutStore.invalidateCheckout()

    if (response?.status === true) {
      await useCartStore().fetch()
    }
  }
})
